import Localize from 'react-intl-universal';

import { format, parseISO, isValid, parse } from 'date-fns';
import { enUS, de } from 'date-fns/locale';
import { isArray, isString } from 'lodash-es';

import * as routerConstants from '@navigation/Constants.js';

import * as globalConstants from './Constants.js';

export const CourseNameLength = 53;

export const getDayName = (date = new Date(), locale = 'en-US') => {
  return date.toLocaleDateString(locale, { weekday: 'short' });
};

export const isAfterToday = (date) => {
  const today = new Date();
  return new Date(date) > today;
};

export const formatLongDateStringToDateObject = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  const dateString = timestamp.substring(0, 10);
  let date = new Date(dateString);
  date.setHours(1, 0);
  return date;
};

// Should be used for dateTime strings which are in this format - YYYY-MM-DDTHH-MM-SS
export const formatLongDateTimeStringToDateTimeObject = (timestamp) => {
  if (!timestamp) return null;

  let browserLanguage =
    navigator.language || (navigator.languages && navigator.languages[0]) || 'en-US';

  const isGerman = browserLanguage.toLowerCase().startsWith('de');

  const locale = isGerman ? de : enUS;

  // 🇩🇪 24h + Uhr
  // 🇬🇧 12h + AM/PM
  // eslint-disable-next-line quotes
  const formatString = isGerman ? "dd.MM.yyyy - HH:mm 'Uhr'" : 'dd.MM.yyyy - hh:mm a';

  return format(parseISO(timestamp), formatString, { locale });
};
// Should be used for dateTime strings which are in this format - YYYY-MM-DD
export const formatDateTimeStringToDateTimeObject = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  return format(parseISO(timestamp), 'dd.MM.yyyy');
};

// Should be used for dateTime strings which are in this format - YYYY-MM-DD
export const formatDateTimeStringToShortMonthDate = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  return format(parseISO(timestamp), 'MMM d');
};

export const formatDateStringToDateObject = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  const dateString = timestamp.substring(0, 8);
  let date = new Date(
    dateString.substring(0, 4) + '-' + dateString.substring(4, 6) + '-' + dateString.substring(6, 8)
  );
  date.setHours(1, 0);
  return date;
};

export const formatDateStringToDateTimeObject = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  const dateString = timestamp.substring(0, 8);
  let date = new Date(
    dateString.substring(0, 4) + '-' + dateString.substring(4, 6) + '-' + dateString.substring(6, 8)
  );
  date.setHours(timestamp.substring(8, 10));
  date.setMinutes(timestamp.substring(10, 12));
  return date;
};

export const formatDateTimeObjectToTimestamp = (date) => {
  if (!date) {
    return '';
  }
  const yearString = date.getFullYear();
  let month = date.getMonth();
  month += 1;
  let monthStr = month.toString();

  if (month < 10) {
    monthStr = '0' + month.toString();
  }

  const day = date.getDate();
  let dayStr = day.toString();

  if (day < 10) {
    dayStr = '0' + day.toString();
  }

  const hours = date.getHours();
  let hoursStr = hours.toString();

  if (hours < 10) {
    hoursStr = '0' + day.toString();
  }

  const minutes = date.getHours();
  let minutesStr = minutes.toString();

  if (minutes < 10) {
    minutesStr = '0' + minutes.toString();
  }

  return yearString + monthStr + dayStr + hoursStr + minutesStr;
};

export const formatDateRange = (start, end) => {
  if (!start || !end) {
    return '';
  }
  const stFormatted = formatDateDMY(start);

  if (start.getTime() === end.getTime()) {
    return stFormatted;
  }

  const endFormatted = formatDateDMY(end);

  return stFormatted + ' - ' + endFormatted;
};

const getDatesMonthAndDate = (date) => {
  if (!date) {
    return '';
  }
  let stDate = date.getDate();
  if (stDate < 10) {
    stDate = '0' + stDate;
  }
  let stMonth = date.getMonth() + 1;
  if (stMonth < 10) {
    stMonth = '0' + stMonth;
  }

  return {
    date: stDate,
    month: stMonth,
    dayName: getDayName(date, window.navigator.languages[0])
  };
};

export const formatDateDMY = (date) => {
  if (!date) {
    return '';
  }

  const dateMonthAndDate = getDatesMonthAndDate(date);
  const stYear = date.getFullYear();
  return dateMonthAndDate.date + '.' + dateMonthAndDate.month + '.' + stYear;
};

export const formatDateTimeStringToDateObject = (dateTimeString) => {
  if (!dateTimeString) {
    return '';
  }

  // If last character is Z then Date function will add 1 hour and time will not be correct
  const getLastChar = dateTimeString.slice(-1);
  return formatDateDMYHM(
    getLastChar === 'Z' ? new Date(dateTimeString.slice(0, -1)) : new Date(dateTimeString)
  );
};

export const formatDateDMYHM = (date) => {
  if (!date) {
    return '';
  }
  const dateMonthAndDate = getDatesMonthAndDate(date);
  const stYear = date.getFullYear();
  let minutes = date.getMinutes();
  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  let hours = date.getHours();
  if (hours < 10) {
    hours = '0' + hours;
  }
  return (
    dateMonthAndDate.date +
    '.' +
    dateMonthAndDate.month +
    '.' +
    stYear +
    '    ' +
    hours +
    ':' +
    minutes
  );
};

export const formatDDMMYYYHHMM = (dateTime, includeHoursAndMinutes = true) => {
  if (!dateTime) {
    return '';
  }
  return format(new Date(dateTime), includeHoursAndMinutes ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy');
};

export const formatDateHHTimeZone = (date) => {
  if (!date) {
    return '';
  }

  let minutes = date.getMinutes();
  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  let hours = date.getHours();

  if (hours < 13) {
    if (hours === 0 && minutes === '00') {
      minutes += ' PM';
    } else {
      minutes += ' AM';
    }
  } else {
    hours -= 12;
    minutes += ' PM';
  }

  let displayHours = hours;
  if (hours < 10) {
    displayHours = '0' + hours;
  }
  return displayHours + ':' + minutes;
};

export const formatDateToTimestamp = (dateObj, isStartDate) => {
  const date = new Date(dateObj);
  const year = date.getFullYear();

  let month = date.getMonth();
  month += 1;
  let monthStr = month.toString();

  if (month < 10) {
    monthStr = '0' + month.toString();
  }

  const day = date.getDate();
  let dayStr = day.toString();

  if (day < 10) {
    dayStr = '0' + day.toString();
  }

  // this method is used to parse start and end date upon search events,
  // if it is start_date set hours and minutes onto '0000' to cover whole day;
  // same goes for end date with value '2359'
  const hoursAndMinutes = isStartDate ? '0000' : '2359';

  return year.toString() + monthStr + dayStr + hoursAndMinutes;
};

export const getRouteDisplayName = (route) => {
  let routeDisplayName;
  switch (route) {
    case routerConstants.routes.path.home:
      routeDisplayName = Localize.get('AppNavBar.home');
      break;
    case routerConstants.routes.path.catalog:
      routeDisplayName = Localize.get('AppNavBar.catalog');
      break;
    case routerConstants.routes.path.myevents:
      routeDisplayName = Localize.get('AppNavBar.myEvents');
      break;
    case routerConstants.routes.path.generalinfo:
      routeDisplayName = Localize.get('AppNavBar.genInfo');
      break;
    case routerConstants.routes.path.signup:
      routeDisplayName = Localize.get('SignIn.signUp');
      break;
    case routerConstants.routes.path.login:
      routeDisplayName = Localize.get('SignIn.logIn');
      break;
    case routerConstants.routes.path.eventDetails:
      routeDisplayName = Localize.get('EventDetails.eventDetails');
      break;
    case routerConstants.routes.path.register:
      routeDisplayName = Localize.get('App.registration');
      break;
    default:
      routeDisplayName = Localize.get('AppNavBar.home');
      break;
  }
  return routeDisplayName;
};

export const setLanguage = () => {
  let appLang = 'EN';
  let browserLang = window.navigator.language;

  if (!browserLang) {
    browserLang = window.navigator.languages[0];
  }

  if (browserLang.includes('de')) {
    appLang = 'DE';
  }

  return appLang;
};

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

const validateAndParseDate = (dateTime) => {
  let isValidDate = false;
  let parsedDate = null;

  if (dateTime && dateTime !== '') {
    if (typeof dateTime === 'string') {
      parsedDate = parseISO(dateTime);
    } else if (dateTime instanceof Date) {
      parsedDate = dateTime;
    }

    isValidDate = isValid(parsedDate);
  }

  return { isValid: isValidDate, parsedDate };
};

export const isValidDate = (value) => {
  if (!value) return false;
  return !new Date(value).toString().includes('Invalid Date');
};

export const dateInitFormats = {
  datePickerFormat: 'MM/dd/yyyy',
  dateTimePickerFormat: 'MM/dd/yyyy HH:mm',
  serverResponseDateFormat: 'yyyy-MM-dd',
  serverResponseDateTimeFormat: 'yyyy-MM-dd HH:mm:ss',
  time: 'HH:mm',
  timeLong: 'HH:mm:ss',
  dmyFormat: 'dd.MM.yyyy'
};

const dateValueToDate = ({ dateValue, format }) => {
  if (typeof dateValue === 'string') {
    return parse(dateValue, format, new Date());
  }

  if (typeof dateValue === 'object' && dateValue instanceof Date) {
    return dateValue;
  }

  return null;
};

export const parseCustomFormatToDate = (dateValue, formats = []) => {
  if (!dateValue) return null;
  const tempFormats = [];
  if (isArray(formats)) {
    tempFormats.push(...formats);
  } else if (isString(formats)) {
    tempFormats.push(formats);
  }
  for (const format of tempFormats) {
    const parsedDate = dateValueToDate({ dateValue, format });

    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }

  return null;
};

// Should be used for dates to format to 'MM/dd/yyyy'
export const formatDateTimeToMDYSlashDelimiter = (dateTime) => {
  const { isValid, parsedDate } = validateAndParseDate(dateTime);

  if (!isValid) return Localize.get('App.invalidDate');

  return format(parsedDate, dateInitFormats.datePickerFormat);
};

// Should be used for dates to format to DD.MM.YYYY
export const formatDateTimeToDMY = (dateTime) => {
  const { isValid, parsedDate } = validateAndParseDate(dateTime);

  if (!isValid) return Localize.get('App.invalidDate');

  return format(parsedDate, 'dd.MM.yyyy');
};

// Should be used for dates to format non iSO dates to YYYY-MM-DD
export const formatNonISODateToYearMonthDay = (dateString) => {
  return format(parse(dateString, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd');
};

export const createAdvancedFilters = (advancedFiltersArray) => {
  if (!advancedFiltersArray) return '';
  return `filters=${encodeURIComponent(JSON.stringify({ advancedFilters: advancedFiltersArray }))}`;
};

export const isEventTypeWebinar = (event) => {
  return event?.type?.key === globalConstants.courseSessionType.webinar;
};

export const showLocation = (event) => {
  if (isEventTypeWebinar(event)) return Localize.get('App.online');

  if (event?.venues?.length > 1) return `${event.venues[0]?.name} + ${event.venues.length - 1}`;

  return event?.venues[0]?.name ?? '';
};

export const getDevice = (width) => {
  if (width < 768) {
    return globalConstants.deviceSize.mobile;
  } else if (width < 992) {
    return globalConstants.deviceSize.tablet;
  } else {
    return globalConstants.deviceSize.computer;
  }
};

export const isNavigatedWithPrivateLink = (location) => {
  if (!location) return false;
  const searchParams = new URLSearchParams(location?.search);
  return searchParams.has('participant') && searchParams.get('isPrivate') === 'true';
};

export const isDisableNavigationOn = (location) => {
  if (!location) return false;
  const searchParams = new URLSearchParams(location?.search);
  return searchParams.get('disableNav') === 'true';
};

export const formatPrice = (price) => {
  if (!price) {
    return 0;
  }
  let browserLanguage = navigator.language || (navigator.languages && navigator.languages[0]);

  const locale = browserLanguage.toLowerCase().startsWith('de') ? de : enUS;

  return parseFloat(price).toLocaleString(locale?.code, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const getPricingDetails = (event) => {
  const eventPrice = formatPrice(event?.price);
  const eventCurrency = event?.standardPrice?.currency?.value || '';
  const isEventPrice0 = !eventPrice || parseInt(eventPrice) === 0;
  return { eventPrice, eventCurrency, isEventPrice0 };
};

export const checkIsEventFree = (event) => {
  return !event?.hasPricesAndDiscountsFeature;
};

export const checkIsEventIlias = (event) => {
  return event?.type?.id === 21;
};

export const generateResourceTypeFromResponse = (response) => {
  const contentType = response?.headers?.['content-type'] || '';
  return contentType.split(';')?.[0].trim() || '';
};

export const isOrbis = () => import.meta.env.VITE_CLIENT_BRAND === 'orbis';

export const isAok = () => import.meta.env.VITE_CLIENT_BRAND === 'aok';

export const isFraunhofer = () => import.meta.env.VITE_CLIENT_BRAND === 'fraunhofer';

export const checkHasHomePage = (data) => {
  switch (import.meta.env.VITE_CLIENT_BRAND) {
    case 'orbis':
    case 'aok':
      return data.find((el) => el.key === 'enable.home.page.features')?.value;
    default:
      return false;
  }
};

export const checkIsCatalogVisible = (data) =>
  data.find((el) => el.key === 'enable.mea.catalog.visibility')?.value || false;

export const checkIsSeatsNumber = (data) =>
  data.find((el) => el.key === 'enable.seat.details.visibility')?.value;

export const seatsInfo = (availableSeats, isSeatsNumber) =>
  availableSeats === 0
    ? Localize.get('Catalog.fullybooked')
    : `${isSeatsNumber ? availableSeats : ''} ${Localize.get('Catalog.seatsAvailable')}`;

export const handleKeyDown = (e, event, onItemClick) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    onItemClick(event);
  }
};

export const setLocalStorage = (key, value) => {
  try {
    let data = window.localStorage.getItem('store') || null;
    data = JSON.parse(data) || {};
    data[key] = value;
    window.localStorage.setItem('store', JSON.stringify(data));
  } catch (error) {
    console.error('Error setting local storage:', error);
  }
};

export const getLocalStorage = (key) => {
  try {
    let data = window.localStorage.getItem('store') || null;
    data = JSON.parse(data);
    if (!data) return null;
    return key in data ? data[key] : null;
  } catch (error) {
    console.error('Error getting local storage:', error);
  }
};

export const concatAddress = (addressObj) => {
  if (!addressObj) return '';

  const { street = '', city = '', number = '' } = addressObj;

  let address = '';

  if (street) {
    address += street;
  }
  if (number) {
    address += address ? `, ${number}` : number;
  }
  if (city) {
    address += address ? `, ${city}` : city;
  }

  return address;
};
